import { useChartColors } from '../lib/accessibility'
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, X, CheckCircle2, FlaskConical, ClipboardList, Clock } from 'lucide-react'
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts'
import { api } from '../lib/api'

const C = {
  bg: 'var(--color-bg)', bgRaised: 'var(--color-bg-card)', bgCard: '#1A2212',
  border: 'var(--color-border)', amber: 'var(--color-primary)', green: '#2D6A2F',
  text: 'var(--color-text)', textMuted: 'var(--color-text-muted)', textFaint: 'var(--color-text-faint)',
  red: '#F87171', blue: '#60A5FA',
}

interface LabTest {
  id: string; test_type: string; field_id?: string; field_name?: string
  lab_name: string; submitted_at: string; status: 'pending' | 'submitted' | 'received' | 'complete'
  sample_depth_cm?: number; sample_location?: string; notes?: string
}

interface TestParameter {
  id: string; parameter: string; value: number; unit: string
  rating: 'deficient' | 'low' | 'optimal' | 'high' | 'excess' | 'toxic'
}

const STATUS_COLORS: Record<string, { bg: string; text: string }> = {
  pending:   { bg: 'var(--color-bg-hover)', text: 'var(--color-text-faint)' },
  submitted: { bg: '#60A5FA22', text: '#60A5FA' },
  received:  { bg: '#F5A62322', text: '#F5A623' },
  complete:  { bg: '#2D6A2F22', text: '#4ADE80' },
}

const RATING_COLORS: Record<string, { bg: string; text: string }> = {
  deficient: { bg: '#F8717122', text: '#F87171' },
  low:       { bg: '#FDBA7422', text: '#FDBA74' },
  optimal:   { bg: '#4ADE8022', text: '#4ADE80' },
  high:      { bg: '#F5A62322', text: '#F5A623' },
  excess:    { bg: '#F8717122', text: '#F87171' },
  toxic:     { bg: '#F8717144', text: '#F87171' },
}

const inputStyle: React.CSSProperties = {
  width: '100%', boxSizing: 'border-box',
  background: C.bg, border: `1px solid ${C.border}`,
  borderRadius: 8, padding: '8px 12px',
  color: C.text, fontSize: 13, outline: 'none',
}
const labelStyle: React.CSSProperties = {
  display: 'block', fontSize: 12, fontWeight: 500, color: C.textMuted, marginBottom: 5,
}

function NewLabTestModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient()
  const [form, setForm] = useState({
    test_type: 'soil', field_id: '', lab_name: '',
    submitted_at: new Date().toISOString().slice(0, 10),
    sample_depth_cm: '', sample_location: '', notes: '',
  })
  const [success, setSuccess] = useState(false)
  const mutation = useMutation({
    mutationFn: (d: Record<string, unknown>) => api.post('/lab-tests', d),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['lab-tests'] }); setSuccess(true); setTimeout(onClose, 1200) },
  })
  function submit(e: React.FormEvent) {
    e.preventDefault()
    mutation.mutate({ ...form, sample_depth_cm: form.sample_depth_cm ? parseInt(form.sample_depth_cm) : undefined, status: 'pending' })
  }
  const set = (k: string, v: string) => setForm(f => ({ ...f, [k]: v }))
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div style={{ background: C.bgRaised, border: `1px solid ${C.border}`, borderRadius: 14, width: 460, maxWidth: '95vw', padding: 24, maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: C.text }}>New Lab Test</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textFaint }}><X size={18} /></button>
        </div>
        {success ? (
          <div style={{ textAlign: 'center', padding: '24px 0', color: '#4ADE80' }}><CheckCircle2 size={36} style={{ marginBottom: 10 }} /><div style={{ fontSize: 15, fontWeight: 600 }}>Lab test created!</div></div>
        ) : (
          <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Test Type</label>
                <select style={inputStyle} value={form.test_type} onChange={e => set('test_type', e.target.value)}>
                  {['soil','water','foliar','compost','feed','tissue'].map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div style={{ flex: 1 }}><label style={labelStyle}>Lab Name *</label><input required style={inputStyle} value={form.lab_name} onChange={e => set('lab_name', e.target.value)} placeholder="e.g. A&L Great Lakes" /></div>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ flex: 1 }}><label style={labelStyle}>Submitted Date</label><input type="date" style={inputStyle} value={form.submitted_at} onChange={e => set('submitted_at', e.target.value)} /></div>
              {form.test_type === 'soil' && (
                <div style={{ flex: 1 }}><label style={labelStyle}>Sample Depth (cm)</label><input type="number" style={inputStyle} value={form.sample_depth_cm} onChange={e => set('sample_depth_cm', e.target.value)} placeholder="15" /></div>
              )}
            </div>
            <div><label style={labelStyle}>Field / Zone</label><input style={inputStyle} value={form.field_id} onChange={e => set('field_id', e.target.value)} placeholder="Field name or ID" /></div>
            <div><label style={labelStyle}>Sample Location</label><input style={inputStyle} value={form.sample_location} onChange={e => set('sample_location', e.target.value)} placeholder="e.g. Northwest corner, 3 cores" /></div>
            <div><label style={labelStyle}>Notes</label><textarea style={{ ...inputStyle, resize: 'vertical', minHeight: 64, fontFamily: 'inherit' }} value={form.notes} onChange={e => set('notes', e.target.value)} /></div>
            {mutation.isError && <div style={{ fontSize: 12, color: C.red, padding: '8px 12px', background: C.red + '11', borderRadius: 8 }}>Failed to save.</div>}
            <button type="submit" disabled={mutation.isPending} style={{ padding: '10px 0', borderRadius: 8, background: C.green, border: 'none', color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
              {mutation.isPending ? 'Saving…' : 'Create Lab Test'}
            </button>
          </form>
        )}
      </div>
    </div>
  )
}

function AddParametersModal({ testId, onClose }: { testId: string; onClose: () => void }) {
  const qc = useQueryClient()
  const [params, setParams] = useState([{ parameter: 'pH', value: '', unit: 'pH', rating: 'optimal' }])
  const [success, setSuccess] = useState(false)
  const mutation = useMutation({
    mutationFn: (d: Record<string, unknown>) => api.post(`/lab-tests/${testId}/parameters`, d),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['lab-test-params', testId] }); setSuccess(true); setTimeout(onClose, 1200) },
  })
  function submit(e: React.FormEvent) {
    e.preventDefault()
    mutation.mutate({ parameters: params.map(p => ({ ...p, value: parseFloat(p.value) })) })
  }
  function addRow() { setParams(p => [...p, { parameter: '', value: '', unit: '', rating: 'optimal' }]) }
  function updateRow(i: number, k: string, v: string) { setParams(p => p.map((r, idx) => idx === i ? { ...r, [k]: v } : r)) }
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div style={{ background: C.bgRaised, border: `1px solid ${C.border}`, borderRadius: 14, width: 560, maxWidth: '95vw', padding: 24, maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: C.text }}>Add Parameters</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textFaint }}><X size={18} /></button>
        </div>
        {success ? (
          <div style={{ textAlign: 'center', padding: '24px 0', color: '#4ADE80' }}><CheckCircle2 size={36} style={{ marginBottom: 10 }} /><div style={{ fontSize: 15, fontWeight: 600 }}>Parameters saved!</div></div>
        ) : (
          <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {params.map((p, i) => (
              <div key={i} style={{ display: 'flex', gap: 8 }}>
                <input style={{ ...inputStyle, flex: 2 }} placeholder="Parameter (e.g. pH)" value={p.parameter} onChange={e => updateRow(i, 'parameter', e.target.value)} />
                <input type="number" step="any" style={{ ...inputStyle, flex: 1 }} placeholder="Value" value={p.value} onChange={e => updateRow(i, 'value', e.target.value)} />
                <input style={{ ...inputStyle, flex: 1 }} placeholder="Unit" value={p.unit} onChange={e => updateRow(i, 'unit', e.target.value)} />
                <select style={{ ...inputStyle, flex: 1 }} value={p.rating} onChange={e => updateRow(i, 'rating', e.target.value)}>
                  {['deficient','low','optimal','high','excess','toxic'].map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
            ))}
            <button type="button" onClick={addRow} style={{ padding: '7px 0', borderRadius: 7, background: 'transparent', border: `1px solid ${C.border}`, color: C.textMuted, fontSize: 12, cursor: 'pointer' }}>
              + Add Row
            </button>
            {mutation.isError && <div style={{ fontSize: 12, color: C.red, padding: '8px 12px', background: C.red + '11', borderRadius: 8 }}>Failed to save.</div>}
            <button type="submit" disabled={mutation.isPending} style={{ padding: '10px 0', borderRadius: 8, background: C.green, border: 'none', color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
              {mutation.isPending ? 'Saving…' : 'Save Parameters'}
            </button>
          </form>
        )}
      </div>
    </div>
  )
}

export function LabTestsPage() {
  const chartC = useChartColors()
  const [showNew, setShowNew] = useState(false)
  const [selectedTest, setSelectedTest] = useState<LabTest | null>(null)
  const [showParams, setShowParams] = useState<string | null>(null)

  const { data: tests = [], isLoading, isError, refetch } = useQuery<LabTest[]>({
    queryKey: ['lab-tests'],
    queryFn: () => api.get('/lab-tests').then(r => r.data?.lab_tests ?? r.data ?? []),
  })

  const { data: params = [], isLoading: paramsLoading } = useQuery<TestParameter[]>({
    queryKey: ['lab-test-params', selectedTest?.id],
    queryFn: () => api.get(`/lab-tests/${selectedTest?.id}/parameters`).then(r => r.data?.parameters ?? r.data ?? []),
    enabled: !!selectedTest,
  })

  const pending = tests.filter(t => t.status === 'pending' || t.status === 'submitted').length
  const complete = tests.filter(t => t.status === 'complete').length
  const testTypes = new Set(tests.map(t => t.test_type)).size

  // Build param bar chart data from selected test params
  const paramChartData = params
    .filter(p => typeof p.value === 'number')
    .slice(0, 10)
    .map(p => ({
      name: p.parameter.length > 12 ? p.parameter.slice(0, 11) + '…' : p.parameter,
      value: p.value,
      rating: p.rating,
      unit: p.unit,
    }))

  const RATING_BAR_COLORS: Record<string, string> = {
    deficient: '#F87171', low: '#FDBA74', optimal: '#4ADE80',
    high: '#F5A623', excess: '#F87171', toxic: '#F87171',
  }

  return (
    <>
      {showNew && <NewLabTestModal onClose={() => setShowNew(false)} />}
      {showParams && <AddParametersModal testId={showParams} onClose={() => setShowParams(null)} />}

      <main id="main-content" style={{ padding: '28px 32px', minHeight: '100%', boxSizing: 'border-box' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: C.text }}>Lab Tests</h1>
            <p style={{ margin: '3px 0 0', fontSize: 13, color: C.textMuted }}>Soil, water, and tissue analysis results</p>
          </div>
          <button onClick={() => setShowNew(true)} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '9px 18px', borderRadius: 9, background: C.green, border: 'none', color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
            <Plus size={16} /> New Lab Test
          </button>
        </div>

        {/* Stat chips */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 24 }}>
          {[
            { label: 'Total tests', value: tests.length, icon: FlaskConical, color: C.amber },
            { label: 'Pending / In lab', value: pending, icon: Clock, color: C.blue },
            { label: 'Completed', value: complete, icon: CheckCircle2, color: '#4ADE80' },
            { label: 'Test types', value: testTypes, icon: ClipboardList, color: C.amber },
          ].map(({ label, value, icon: Icon, color }) => (
            <div key={label} style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 10, padding: '14px 18px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <Icon size={14} color={color} />
                <span style={{ fontSize: 11, color: C.textMuted }}>{label}</span>
              </div>
              <div style={{ fontSize: 22, fontWeight: 700, color: C.text }}>{value}</div>
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start' }}>
          {/* Left: Tests List */}
          <div style={{ flex: 1, minWidth: 0 }}>
            {isLoading ? (
              <div style={{ textAlign: 'center', padding: '60px 0', color: C.textFaint }}>Loading tests…</div>
            ) : isError ? (
              <div style={{ textAlign: 'center', padding: '60px 0' }}>
                <p style={{ color: C.red, marginBottom: 12 }}>Failed to load lab tests</p>
                <button onClick={() => refetch()} style={{ padding: '8px 16px', borderRadius: 8, background: C.bgCard, border: `1px solid ${C.border}`, color: C.text, cursor: 'pointer' }}>Retry</button>
              </div>
            ) : tests.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '60px 0' }}>
                <FlaskConical size={40} style={{ color: C.textFaint, opacity: 0.4, marginBottom: 12 }} />
                <p style={{ margin: 0, color: C.textFaint }}>No lab tests yet</p>
                <p style={{ margin: '6px 0 0', fontSize: 12, color: C.textFaint }}>Click "New Lab Test" to track soil and water analysis</p>
              </div>
            ) : (
              <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 12, overflow: 'hidden' }}>
                {tests.map((test, i) => {
                  const sc = STATUS_COLORS[test.status] ?? STATUS_COLORS.pending
                  const isSelected = selectedTest?.id === test.id
                  return (
                    <div
                      key={test.id}
                      onClick={() => setSelectedTest(isSelected ? null : test)}
                      style={{ padding: '14px 18px', borderBottom: i < tests.length - 1 ? `1px solid ${C.border}` : 'none', cursor: 'pointer', background: isSelected ? 'color-mix(in srgb, var(--color-primary) 5%, transparent)' : 'transparent', display: 'flex', alignItems: 'center', gap: 12 }}
                    >
                      <FlaskConical size={16} style={{ color: C.textFaint, flexShrink: 0 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                          <span style={{ fontSize: 14, fontWeight: 600, color: C.text }}>{test.test_type} test</span>
                          <span style={{ fontSize: 11, padding: '2px 7px', borderRadius: 5, background: sc.bg, color: sc.text, fontWeight: 600 }}>{test.status}</span>
                        </div>
                        <div style={{ fontSize: 12, color: C.textMuted }}>
                          {test.lab_name} {test.field_name ? `· ${test.field_name}` : ''}
                        </div>
                      </div>
                      <div style={{ fontSize: 12, color: C.textFaint, flexShrink: 0 }}>
                        {new Date(test.submitted_at).toLocaleDateString([], { month: 'short', day: 'numeric' })}
                      </div>
                      {(test.status === 'received' || test.status === 'complete') && (
                        <button
                          onClick={e => { e.stopPropagation(); setShowParams(test.id) }}
                          style={{ padding: '4px 10px', borderRadius: 6, background: 'transparent', border: `1px solid ${C.border}`, color: C.textMuted, fontSize: 11, cursor: 'pointer', flexShrink: 0 }}
                        >
                          + Params
                        </button>
                      )}
                    </div>
                  )
                })}
              </div>
            )}
          </div>

          {/* Right: Parameter Details */}
          <div style={{ width: 320, flexShrink: 0 }}>
            <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 12, padding: '0 16px 16px' }}>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: C.textFaint, padding: '12px 0 8px' }}>
                {selectedTest ? `${selectedTest.test_type.toUpperCase()} — ${selectedTest.lab_name}` : 'Select a test'}
              </div>
              {!selectedTest ? (
                <p style={{ fontSize: 12, color: C.textFaint, margin: '4px 0' }}>Click a test to view its parameters.</p>
              ) : paramsLoading ? (
                <div style={{ fontSize: 12, color: C.textFaint }}>Loading parameters…</div>
              ) : params.length === 0 ? (
                <div>
                  <p style={{ fontSize: 12, color: C.textFaint, margin: '4px 0 12px' }}>No parameters recorded yet.</p>
                  {(selectedTest.status === 'received' || selectedTest.status === 'complete') && (
                    <button onClick={() => setShowParams(selectedTest.id)} style={{ padding: '7px 14px', borderRadius: 7, background: 'transparent', border: `1px solid ${C.border}`, color: C.textMuted, fontSize: 12, cursor: 'pointer' }}>
                      Add Parameters
                    </button>
                  )}
                </div>
              ) : (
                <>
                  {paramChartData.length > 1 && (
                    <div style={{ marginBottom: 14 }}>
                      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: C.textFaint, marginBottom: 8 }}>Values Overview</div>
                      <ResponsiveContainer width="100%" height={Math.min(paramChartData.length * 26 + 8, 200)}>
                        <BarChart data={paramChartData} layout="vertical" margin={{ top: 0, right: 28, bottom: 0, left: 0 }}>
                          <XAxis type="number" hide />
                          <YAxis type="category" dataKey="name" tick={{ fill: chartC.muted, fontSize: 11 }} axisLine={false} tickLine={false} width={80} />
                          <Tooltip
                            contentStyle={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 12 }}
                            formatter={(val: number) => [`${val}`, 'Value']}
                            cursor={{ fill: 'rgba(255,255,255,0.04)' }}
                          />
                          <Bar dataKey="value" radius={[0, 3, 3, 0]} barSize={12}>
                            {paramChartData.map((entry, i) => (
                              <Cell key={i} fill={RATING_BAR_COLORS[entry.rating] ?? '#4ADE80'} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        {['Parameter','Value','Rating'].map(h => <th key={h} style={{ textAlign: 'left', fontSize: 10, fontWeight: 700, color: C.textFaint, padding: '4px 0', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {params.map(p => {
                        const rc = RATING_COLORS[p.rating] ?? RATING_COLORS.optimal
                        return (
                          <tr key={p.id} style={{ borderBottom: `1px solid ${C.border}` }}>
                            <td style={{ padding: '8px 4px 8px 0', fontSize: 13, color: C.text, fontWeight: 500 }}>{p.parameter}</td>
                            <td style={{ padding: '8px 4px', fontSize: 13, color: C.textMuted }}>{p.value} {p.unit}</td>
                            <td style={{ padding: '8px 0 8px 4px' }}>
                              <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 5, background: rc.bg, color: rc.text }}>{p.rating}</span>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </>
              )}
            </div>
          </div>
        </div>
      </main>
    </>
  )
}
